import { useEffect, useEffectEvent, useRef } from "react";
import type { JSX } from "react";
import { useOnlineStatus } from "../../offline/useOnlineStatus";
import { useI18n } from "../../shared/i18n/I18nContext";
import { ErrorMessage } from "../../shared/ui/ErrorMessage";
import { PublicLayout } from "../../shared/ui/PublicLayout";
import styles from "../../shared/ui/ui.module.css";

/** Properties of {@link PlatformAccessUnavailablePage}. */
export interface PlatformAccessUnavailablePageProps {
  /** The error of the last request, or `null` while waiting for a connection. */
  readonly error: Error | null;
  /** Whether the server is asked again at the moment. */
  readonly retrying: boolean;
  /** Asks the server again. */
  readonly onRetry: () => void;
}

/**
 * Shown by the gate when `GET /platform/v1/me` failed (network, server) or cannot run offline, and
 * the device neither remembers the access of the person nor holds data of them from the application
 * of the tenants (F-84). Whether the operator area or the application of the tenants opens is decided
 * on a settled answer only, so operator staff never see the clock because the server was not
 * reached. The page explains this, names the error with its code (or that the device is offline) and
 * offers „Erneut versuchen“. It asks the server again by itself when the device reports the
 * connection back (`useOnlineStatus`): the query library starts as online and does not follow that
 * status, so a start without connection ends in a failed request that would not be repeated. Like
 * every public screen it offers „Abmelden“, which warns about unsent entries on the device.
 *
 * @param props - Component properties.
 * @returns The page.
 */
export function PlatformAccessUnavailablePage(
  props: PlatformAccessUnavailablePageProps,
): JSX.Element {
  const { t } = useI18n();
  const online = useOnlineStatus();
  const wasOnlineRef = useRef(online);
  const retryOnReconnect = useEffectEvent(() => {
    props.onRetry();
  });

  useEffect(() => {
    if (online && !wasOnlineRef.current) {
      retryOnReconnect();
    }

    wasOnlineRef.current = online;
  }, [online]);

  const page = (
    <PublicLayout title={t("platform.access.title")}>
      <p>{t("platform.access.text")}</p>
      {!online ? (
        <p>{t("platform.access.offline")}</p>
      ) : props.error === null ? null : (
        <ErrorMessage error={props.error} />
      )}
      <div className={styles["row"]}>
        <button
          type="button"
          className={styles["button"]}
          disabled={props.retrying}
          onClick={props.onRetry}
        >
          {props.retrying ? t("error.retrying") : t("error.retry")}
        </button>
      </div>
    </PublicLayout>
  );

  return page;
}
